import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AuthContext } from "@/App";
import AdminNavBar from "@/components/AdminNavBar";
import AppFooter from "@/components/AppFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, MapPin, Flag, Search, Download, Globe } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyTeeHoles = () =>
  Array.from({ length: 18 }, (_, i) => ({
    hole: i + 1,
    par: 4,
    stroke_index: null,
    yardage: null,
  }));

const newTee = (overrides = {}) => ({
  id: `tee-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  name: "",
  gender: "any",
  course_rating: null,
  slope_rating: null,
  bogey_rating: null,
  total_yards: null,
  par_total: 72,
  holes: emptyTeeHoles(),
  ...overrides,
});

const recomputeParTotal = (tee) => {
  const t = (tee.holes || []).reduce((s, h) => s + (parseInt(h.par) || 0), 0);
  return { ...tee, par_total: t };
};

export default function AdminCourses() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [clubName, setClubName] = useState("");
  const [location, setLocation] = useState("");
  const [tees, setTees] = useState([]);
  const [activeTeeIdx, setActiveTeeIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  // Import flow
  const [showImport, setShowImport] = useState(false);
  const [importProvider, setImportProvider] = useState("ukgolf"); // "ukgolf" or "golfcourseapi"
  const [importQuery, setImportQuery] = useState("");
  const [importHits, setImportHits] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importingId, setImportingId] = useState(null);
  // For UK provider: when a club has multiple courses, let admin pick
  const [pickerHit, setPickerHit] = useState(null);
  const [pickerCourses, setPickerCourses] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (user.role !== "admin") {
      navigate("/dashboard");
      return;
    }
    fetchCourses();
  }, [user]);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/admin/courses`, { withCredentials: true });
      setCourses(r.data);
    } catch {
      toast.error("Failed to load courses");
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing({});
    setName("");
    setClubName("");
    setLocation("");
    setTees([newTee({ name: "Men's", gender: "men" })]);
    setActiveTeeIdx(0);
  };

  const openEdit = (course) => {
    setEditing(course);
    setName(course.name || "");
    setClubName(course.club_name || "");
    setLocation(course.location || "");
    const teeList = (course.tees || []).map((t) => ({
      ...newTee({}),
      ...t,
      holes: emptyTeeHoles().map((h) => {
        const f = (t.holes || []).find((x) => x.hole === h.hole);
        return f ? { ...h, ...f } : h;
      }),
    }));
    setTees(teeList.length ? teeList : [newTee({ name: "Men's", gender: "men" })]);
    setActiveTeeIdx(0);
  };

  const closeDialog = () => setEditing(null);

  const updateTee = (idx, patch) => {
    setTees((prev) =>
      prev.map((t, i) => (i === idx ? recomputeParTotal({ ...t, ...patch }) : t))
    );
  };

  const updateTeeHole = (idx, holeIdx, field, raw) => {
    setTees((prev) =>
      prev.map((t, i) => {
        if (i !== idx) return t;
        const holes = t.holes.map((h, j) => {
          if (j !== holeIdx) return h;
          const numericMax = field === "par" ? 7 : field === "stroke_index" ? 18 : 9999;
          const numericMin = field === "par" ? 3 : 1;
          if (raw === "" || raw === null) return { ...h, [field]: null };
          const v = Math.max(numericMin, Math.min(numericMax, parseInt(raw) || 0));
          return { ...h, [field]: v || null };
        });
        return recomputeParTotal({ ...t, holes });
      })
    );
  };

  const addTee = () => {
    setTees((prev) => {
      const next = [...prev, newTee({ name: "New tee", gender: "any" })];
      setActiveTeeIdx(next.length - 1);
      return next;
    });
  };

  const removeTee = (idx) => {
    if (tees.length <= 1) {
      toast.error("A course must have at least one tee");
      return;
    }
    setTees((prev) => prev.filter((_, i) => i !== idx));
    setActiveTeeIdx(0);
  };

  const handleSave = async () => {
    if (!name.trim()) return toast.error("Course name is required");
    if (!tees.length) return toast.error("Add at least one tee");
    if (tees.some((t) => !t.name.trim())) return toast.error("Every tee needs a name");

    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        club_name: clubName.trim() || null,
        location: location.trim() || null,
        tees: tees.map((t) => ({
          id: t.id,
          name: t.name.trim(),
          gender: t.gender || "any",
          course_rating: t.course_rating ? parseFloat(t.course_rating) : null,
          slope_rating: t.slope_rating ? parseInt(t.slope_rating) : null,
          bogey_rating: t.bogey_rating ? parseFloat(t.bogey_rating) : null,
          total_yards: t.total_yards ? parseInt(t.total_yards) : null,
          holes: t.holes.map((h) => ({
            hole: h.hole,
            par: parseInt(h.par) || 4,
            stroke_index: h.stroke_index ? parseInt(h.stroke_index) : null,
            yardage: h.yardage ? parseInt(h.yardage) : null,
          })),
        })),
      };
      if (editing && editing.id) {
        await axios.put(`${API}/admin/courses/${editing.id}`, payload, { withCredentials: true });
        toast.success("Course updated");
      } else {
        await axios.post(`${API}/admin/courses`, payload, { withCredentials: true });
        toast.success("Course created");
      }
      closeDialog();
      fetchCourses();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save course");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (course) => {
    if (!window.confirm(`Delete "${course.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/courses/${course.id}`, { withCredentials: true });
      toast.success("Course deleted");
      fetchCourses();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete course");
    }
  };

  // ----- Import flow -----
  const runImportSearch = async () => {
    if (!importQuery.trim()) return;
    setImportLoading(true);
    setImportHits([]);
    try {
      const r = await axios.get(`${API}/admin/courses/search`, {
        params: { q: importQuery.trim(), provider: importProvider },
        withCredentials: true,
      });
      setImportHits(r.data?.results || []);
      if (!(r.data?.results || []).length) toast.info("No matches found");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally {
      setImportLoading(false);
    }
  };

  const handleImport = async (hit, courseId = null) => {
    setImportingId(hit.provider_id);
    try {
      const payload = { provider_id: hit.provider_id, provider: importProvider };
      if (courseId) payload.course_id = courseId;
      await axios.post(
        `${API}/admin/courses/import`,
        payload,
        { withCredentials: true }
      );
      toast.success(`Imported "${hit.club_name}${hit.course_name ? ` — ${hit.course_name}` : ""}"`);
      setShowImport(false);
      setImportQuery("");
      setImportHits([]);
      setPickerHit(null);
      setPickerCourses([]);
      fetchCourses();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setImportingId(null);
    }
  };

  const startUkImport = async (hit) => {
    // For UK provider: fetch courses at this club; if 1 → import directly, else show picker
    if (importProvider !== "ukgolf") {
      handleImport(hit);
      return;
    }
    setPickerHit(hit);
    setPickerLoading(true);
    setPickerCourses([]);
    try {
      const r = await axios.get(`${API}/admin/courses/club-courses`, {
        params: { club_id: hit.provider_id, provider: "ukgolf" },
        withCredentials: true,
      });
      const list = r.data?.courses || [];
      if (list.length === 0) {
        toast.error("No course data available for this club");
        setPickerHit(null);
      } else if (list.length === 1) {
        setPickerHit(null);
        handleImport(hit, list[0].course_id);
      } else {
        setPickerCourses(list);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load courses for club");
      setPickerHit(null);
    } finally {
      setPickerLoading(false);
    }
  };

  const activeTee = tees[activeTeeIdx];

  if (!user || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-ink-muted text-sm">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app flex flex-col">
      <AdminNavBar />
      <main className="flex-1 container mx-auto px-4 sm:px-8 py-8 max-w-5xl">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-8">
          <div>
            <p className="ge-eyebrow">Course library</p>
            <h1 className="ge-page-title mt-1">Courses</h1>
            <p className="ge-page-sub mt-1">Multi-tee setup. Capture par, stroke index and yardage per tee. Optional but unlocks par-aware leaderboards.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setShowImport(true)} data-testid="import-course-btn">
              <Globe className="w-4 h-4 mr-1.5" /> Import from API
            </Button>
            <Button onClick={openCreate} className="bg-ink hover:bg-ink-hover" data-testid="add-course-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New course
            </Button>
          </div>
        </div>

        {courses.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <Flag className="w-8 h-8 text-ink-soft mx-auto mb-3" />
              <p className="text-ink font-medium">No courses yet</p>
              <p className="text-sm text-ink-muted mt-1 mb-5">Search worldwide via GolfCourseAPI, or build a course manually.</p>
              <div className="flex justify-center gap-2">
                <Button variant="outline" onClick={() => setShowImport(true)}>
                  <Globe className="w-4 h-4 mr-1.5" /> Search & import
                </Button>
                <Button onClick={openCreate} className="bg-ink hover:bg-ink-hover">
                  <Plus className="w-4 h-4 mr-1.5" /> Add manually
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="border border-line rounded-xl bg-surface overflow-hidden divide-y divide-line">
            {courses.map((c) => (
              <div
                key={c.id}
                data-testid={`course-row-${c.id}`}
                className="flex items-center justify-between gap-4 px-5 py-4 hover:bg-app transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-ink tracking-tight truncate">{c.name}</span>
                    {c.club_name && <span className="text-[12.5px] text-ink-muted truncate">— {c.club_name}</span>}
                    {c.is_test && (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-warm-tint text-warm font-semibold">Test</span>
                    )}
                    {(c.provider === "golfcourseapi" || c.provider === "ukgolf") && (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-accent-tint text-accent-deep font-semibold flex items-center gap-1">
                        <Globe className="w-3 h-3" /> {c.provider === "ukgolf" ? "UK API" : "Imported"}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-[12.5px] text-ink-muted flex-wrap">
                    {c.location && (
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5" /> {c.location}
                      </span>
                    )}
                    <span>
                      <span className="text-ink">{(c.tees || []).length}</span> tee{(c.tees || []).length === 1 ? "" : "s"}
                    </span>
                    {(c.tees || [])[0]?.par_total && (
                      <span className="font-mono tabular-nums">par {c.tees[0].par_total}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button variant="outline" size="sm" onClick={() => openEdit(c)} data-testid={`edit-course-${c.id}-btn`}>
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDelete(c)} data-testid={`delete-course-${c.id}-btn`}>
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      <AppFooter />

      {/* ===== Create / Edit dialog ===== */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing && editing.id ? "Edit course" : "New course"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label htmlFor="course-name">Course</Label>
                <Input id="course-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. The West Course" data-testid="course-name-input" />
              </div>
              <div>
                <Label htmlFor="course-club">Club</Label>
                <Input id="course-club" value={clubName} onChange={(e) => setClubName(e.target.value)} placeholder="e.g. Royal Cinque Ports" data-testid="course-club-input" />
              </div>
              <div>
                <Label htmlFor="course-location">Location</Label>
                <Input id="course-location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Deal, Kent" data-testid="course-location-input" />
              </div>
            </div>

            {/* Tees */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="ge-eyebrow">Tees</p>
                <Button variant="outline" size="sm" onClick={addTee} data-testid="add-tee-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add tee
                </Button>
              </div>

              {/* Tee tabs */}
              <div className="flex flex-wrap gap-1.5 mb-3">
                {tees.map((t, i) => (
                  <button
                    type="button"
                    key={t.id}
                    onClick={() => setActiveTeeIdx(i)}
                    data-testid={`tee-tab-${i}`}
                    className={`px-3 py-1.5 rounded-md text-[12.5px] font-medium border transition-colors ${
                      i === activeTeeIdx
                        ? "bg-ink text-white border-ink"
                        : "bg-surface text-ink-muted border-line hover:border-line-strong hover:text-ink"
                    }`}
                  >
                    {t.name || "Untitled"}
                    <span className="ml-1.5 text-[10px] opacity-70 uppercase tracking-wider">{t.gender}</span>
                  </button>
                ))}
              </div>

              {activeTee && (
                <div className="border border-line rounded-lg p-4 bg-app/40">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
                    <div>
                      <Label>Tee name</Label>
                      <Input
                        value={activeTee.name}
                        onChange={(e) => updateTee(activeTeeIdx, { name: e.target.value })}
                        placeholder="White / Red / Blue …"
                        data-testid={`tee-${activeTeeIdx}-name`}
                      />
                    </div>
                    <div>
                      <Label>Gender</Label>
                      <select
                        value={activeTee.gender}
                        onChange={(e) => updateTee(activeTeeIdx, { gender: e.target.value })}
                        data-testid={`tee-${activeTeeIdx}-gender`}
                        className="w-full px-3 py-2 border border-line rounded-lg bg-surface text-ink text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent"
                      >
                        <option value="any">Any / Unisex</option>
                        <option value="men">Men's</option>
                        <option value="ladies">Ladies'</option>
                      </select>
                    </div>
                    <div>
                      <Label>Course Rating</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={activeTee.course_rating || ""}
                        onChange={(e) => updateTee(activeTeeIdx, { course_rating: e.target.value })}
                        placeholder="e.g. 71.5"
                      />
                    </div>
                    <div>
                      <Label>Slope Rating</Label>
                      <Input
                        type="number"
                        value={activeTee.slope_rating || ""}
                        onChange={(e) => updateTee(activeTeeIdx, { slope_rating: e.target.value })}
                        placeholder="e.g. 132"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-2">
                    <p className="ge-eyebrow">Par · stroke index per hole</p>
                    <p className="text-[12.5px] text-ink-muted">
                      Total par <span className="font-mono tabular-nums font-semibold text-ink">{activeTee.par_total || 0}</span>
                    </p>
                  </div>

                  {[0, 9].map((offset) => (
                    <div key={offset} className="grid grid-cols-9 gap-1 mb-1">
                      {activeTee.holes.slice(offset, offset + 9).map((h, j) => (
                        <div key={h.hole} className="bg-surface border border-line rounded-md p-1.5 flex flex-col items-center">
                          <span className="text-[10px] text-ink-soft font-semibold">H{h.hole}</span>
                          <input
                            type="number"
                            min={3}
                            max={7}
                            value={h.par || ""}
                            onChange={(e) => updateTeeHole(activeTeeIdx, offset + j, "par", e.target.value)}
                            data-testid={`tee-${activeTeeIdx}-hole-${h.hole}-par`}
                            className="w-full text-center font-mono tabular-nums font-semibold text-ink bg-app rounded mt-0.5 px-1 py-1 focus:outline-none focus:ring-1 focus:ring-accent text-sm"
                            placeholder="par"
                          />
                          <input
                            type="number"
                            min={1}
                            max={18}
                            value={h.stroke_index || ""}
                            onChange={(e) => updateTeeHole(activeTeeIdx, offset + j, "stroke_index", e.target.value)}
                            data-testid={`tee-${activeTeeIdx}-hole-${h.hole}-si`}
                            className="w-full text-center font-mono tabular-nums text-ink-muted bg-transparent rounded mt-1 px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-accent text-[11px] border border-line"
                            placeholder="SI"
                          />
                        </div>
                      ))}
                    </div>
                  ))}

                  <p className="text-[11.5px] text-ink-soft mt-2">Par per hole on top, stroke index below. Leave SI blank if not known — Stableford and net countback need it but Eclectic doesn't.</p>

                  {tees.length > 1 && (
                    <div className="mt-4 flex justify-end">
                      <Button variant="outline" size="sm" onClick={() => removeTee(activeTeeIdx)} data-testid={`tee-${activeTeeIdx}-remove`}>
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove this tee
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={closeDialog}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-ink hover:bg-ink-hover" data-testid="save-course-btn">
              {saving ? "Saving…" : (editing && editing.id ? "Save changes" : "Create course")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Import dialog ===== */}
      <Dialog open={showImport} onOpenChange={(open) => { setShowImport(open); if (!open) { setPickerHit(null); setPickerCourses([]); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Import course from API</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Provider switcher */}
            <div className="flex gap-1 p-1 bg-black/[0.04] rounded-lg w-fit">
              <button
                type="button"
                onClick={() => { setImportProvider("ukgolf"); setImportHits([]); setImportQuery(""); }}
                data-testid="provider-ukgolf"
                className={`px-3.5 py-1.5 rounded-md text-[12.5px] font-medium transition-colors ${
                  importProvider === "ukgolf"
                    ? "bg-surface text-ink shadow-sm ring-1 ring-line"
                    : "bg-transparent text-ink-muted hover:text-ink"
                }`}
              >
                UK Golf 🇬🇧
              </button>
              <button
                type="button"
                onClick={() => { setImportProvider("golfcourseapi"); setImportHits([]); setImportQuery(""); }}
                data-testid="provider-golfcourseapi"
                className={`px-3.5 py-1.5 rounded-md text-[12.5px] font-medium transition-colors ${
                  importProvider === "golfcourseapi"
                    ? "bg-surface text-ink shadow-sm ring-1 ring-line"
                    : "bg-transparent text-ink-muted hover:text-ink"
                }`}
              >
                Worldwide 🌍
              </button>
            </div>

            <p className="text-sm text-ink-muted">
              {importProvider === "ukgolf"
                ? "Search 2,668 UK clubs (England, Scotland, Wales, NI). Imports include WHS slope/course ratings and hole-by-hole par + stroke index per tee."
                : "Search ~30,000 worldwide courses. Imported courses bring all tees with par, stroke index, yardage, CR and SR. You can edit anything afterwards."}
            </p>
            <form
              onSubmit={(e) => { e.preventDefault(); runImportSearch(); }}
              className="flex gap-2"
            >
              <Input
                value={importQuery}
                onChange={(e) => setImportQuery(e.target.value)}
                placeholder={importProvider === "ukgolf" ? "e.g. Royal Cinque Ports / Sunningdale / St Andrews" : "e.g. Pinehurst / Augusta National"}
                data-testid="import-search-input"
                autoFocus
              />
              <Button type="submit" disabled={importLoading || !importQuery.trim()} className="bg-ink hover:bg-ink-hover" data-testid="import-search-btn">
                <Search className="w-4 h-4 mr-1.5" />
                {importLoading ? "Searching…" : "Search"}
              </Button>
            </form>

            {importHits.length > 0 && !pickerHit && (
              <div className="border border-line rounded-lg overflow-hidden divide-y divide-line max-h-80 overflow-y-auto">
                {importHits.map((h) => (
                  <div key={h.provider_id} className="flex items-start gap-3 px-4 py-3 hover:bg-app">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-ink truncate">{h.course_name || h.club_name}</p>
                      <p className="text-[12.5px] text-ink-muted truncate">
                        {h.club_name && h.club_name !== h.course_name ? h.club_name : ""}
                        {h.address ? ` · ${h.address}` : ""}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => startUkImport(h)}
                      disabled={importingId === h.provider_id || pickerLoading}
                      className="bg-accent hover:bg-accent-deep border-accent text-white"
                      data-testid={`import-hit-${h.provider_id}`}
                    >
                      <Download className="w-3.5 h-3.5 mr-1" />
                      {importingId === h.provider_id ? "Importing…" : (pickerLoading ? "Loading…" : "Import")}
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Course picker when a UK club has multiple courses */}
            {pickerHit && pickerCourses.length > 0 && (
              <div className="border border-line rounded-lg overflow-hidden bg-app/40">
                <div className="px-4 py-3 border-b border-line bg-surface flex items-center justify-between">
                  <div>
                    <p className="text-[13.5px] font-semibold text-ink">{pickerHit.club_name}</p>
                    <p className="text-[11.5px] text-ink-muted">Pick which course to import</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => { setPickerHit(null); setPickerCourses([]); }}>
                    Cancel
                  </Button>
                </div>
                <div className="divide-y divide-line max-h-72 overflow-y-auto">
                  {pickerCourses.map((c) => (
                    <div key={c.course_id} className="flex items-center gap-3 px-4 py-3 bg-surface">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-ink truncate">{c.name}</p>
                        <p className="text-[12px] text-ink-muted">
                          {c.holes ? `${c.holes} holes` : "—"}
                          {c.par ? ` · par ${c.par}` : ""}
                          {c.tees_count ? ` · ${c.tees_count} tees` : ""}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleImport(pickerHit, c.course_id)}
                        disabled={importingId === pickerHit.provider_id}
                        className="bg-accent hover:bg-accent-deep border-accent text-white"
                        data-testid={`import-pick-${c.course_id}`}
                      >
                        <Download className="w-3.5 h-3.5 mr-1" />
                        {importingId === pickerHit.provider_id ? "Importing…" : "Import"}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!importLoading && importQuery && importHits.length === 0 && (
              <p className="text-sm text-ink-soft text-center py-4">Search to see results.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
